import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '../components/MuiIcons';
import { api } from '../api';

type Mode = 'signin' | 'signup' | 'forgot';

const SignInPage: React.FC = () => {
  const history = useHistory();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteEmailDisabled, setInviteEmailDisabled] = useState(false);

  // Check if user is already signed in on mount
  useEffect(() => {
    const checkAuth = () => {
      const savedAuth = localStorage.getItem('auth');
      if (savedAuth) {
        const { user, expiresAt } = JSON.parse(savedAuth);
        const now = new Date().getTime();
        
        if (now < expiresAt) {
          // Still within 30 days, redirect to home
          localStorage.setItem('user', JSON.stringify(user));
          history.push('/home');
        } else {
          // Expired, clear storage
          localStorage.removeItem('auth');
          localStorage.removeItem('user');
        }
      }
    };

    checkAuth();

    // If an invite token is present in the URL, prefill signup email and switch to signup
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    if (token) {
      setInviteToken(token);
      (async () => {
        try {
          const resp = await fetch(api(`/api/invites/${token}`));
          if (!resp.ok) {
            const d = await resp.json().catch(() => ({}));
            setInviteError(d.message || 'Invalid invite');
            return;
          }
          const d = await resp.json();
          if (d && d.email) {
            setEmail(d.email || '');
            setInviteEmailDisabled(true);
            // Ensure name and password are empty when arriving from an invite link
            setName('');
            setPassword('');
            setShowPassword(false);
            setMode('signup');
            setInviteMessage('You were invited — please complete signup to join');
          } else {
            setInviteError('Invite did not contain an email');
          }
        } catch (err) {
          console.warn('Failed to fetch invite details', err);
          setInviteError('Failed to fetch invite');
        }
      })();
    }

    // Listen for storage changes (sign out in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth' && e.newValue === null) {
        // Auth was removed in another tab, stay on sign-in page
        localStorage.removeItem('user');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [history]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: Mode) => {
    setMode(newValue);
    setError('');
    setMessage('');
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const response = await fetch(api('/api/auth/signin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Sign in failed');
      } else {
        setMessage('Sign in successful!');
        setPassword('');
        if (data && data.id) {
          localStorage.setItem('user', JSON.stringify(data));
          // If there is an invite token in the URL, accept it for this user
          const params = new URLSearchParams(window.location.search);
          const inviteToken = params.get('invite');
          if (inviteToken) {
            try {
              const resp = await fetch(api('/api/invites/accept'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: inviteToken, userId: data.id }),
              });
              const respData = await resp.json().catch(() => ({}));
              if (resp.ok) {
                setInviteMessage(respData.message || 'Invite accepted');
              } else {
                setInviteError(respData.message || 'Failed to accept invite');
              }
            } catch (err) {
              console.warn('Failed to accept invite after sign in', err);
              setInviteError('Failed to accept invite');
            }
          }
        } else {
          localStorage.removeItem('user');
        }
        // If "Keep me signed in" is checked, store auth with 30-day expiry
        if (keepSignedIn && data && data.id) {
          const expiresAt = new Date().getTime() + (30 * 24 * 60 * 60 * 1000); // 30 days
          localStorage.setItem('auth', JSON.stringify({
            user: data,
            expiresAt
          }));
        }
        history.push('/home');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!name || !email || !password) {
      setError('Name, email, and password are required.');
      return;
    }
    try {
      // Include invite token (if present in URL) so backend can auto-accept
      const params = new URLSearchParams(window.location.search);
      const inviteToken = params.get('invite');
      const body: any = { name, email, password };
      if (inviteToken) body.invite = inviteToken;

      const response = await fetch(api('/api/auth/signin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        // If signup blocked due to a pending invite, prompt user to use Forgot Password
        if (data && data.code === 'invite_pending') {
          setMessage('An invite was sent to this email. We can resend the invite link so you can complete signup.');
          setMode('forgot');
          setEmail(email);
          return;
        }
        setError(data.error || 'Sign up failed');
      } else {
        setMessage('Account created! You can now sign in.');
        setMode('signin');
        setName('');
        setEmail('');
        setPassword('');
        if (data && data.id) {
          localStorage.setItem('user', JSON.stringify(data));
          // If the backend returned acceptedInvitation info, redirect there
          if (data.acceptedInvitation && data.acceptedInvitation.competitionId) {
            history.push(`/competitions/${data.acceptedInvitation.competitionId}`);
            return;
          }
        } else {
          localStorage.removeItem('user');
        }
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleForgot = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!email) {
      setError('Email is required.');
      return;
    }
    // Call backend to resend invite / send reset link
    (async () => {
      try {
        const resp = await fetch(api('/api/auth/forgot'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const d = await resp.json().catch(() => ({}));
        if (resp.ok) {
          setMessage(d.message || `If an account exists for ${email}, we've sent a link.`);
        } else {
          setError(d.error || 'Failed to send link');
        }
      } catch (err) {
        setError('Network error');
      }
    })();
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Welcome to Wine War!
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Please sign in or create an account to get started.
        </Typography>
        <Tabs
          value={mode}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Sign In" value="signin" />
          <Tab label="Sign Up" value="signup" />
          <Tab label="Forgot Password" value="forgot" />
        </Tabs>
        <Box
          component="form"
          onSubmit={
            mode === 'signin'
              ? handleSignIn
              : mode === 'signup'
              ? handleSignUp
              : handleForgot
          }
          sx={{ mt: 2 }}
        >
          {mode === 'signup' && (
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={name}
              onChange={e => setName(e.target.value)}
              autoComplete={inviteToken ? 'off' : 'name'}
            />
          )}
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={mode === 'signup' && inviteEmailDisabled}
            autoComplete="email"
          />
          {(mode === 'signin' || mode === 'signup') && (
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={inviteToken ? 'new-password' : (mode === 'signup' ? 'new-password' : 'current-password')}
              inputProps={{
                name: inviteToken ? 'pw-invite' : 'password',
                autoComplete: inviteToken ? 'new-password' : undefined,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {mode === 'signin' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={keepSignedIn}
                  onChange={e => setKeepSignedIn(e.target.checked)}
                />
              }
              label="Keep me signed in for 30 days"
              sx={{ mt: 1 }}
            />
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
          >
            {mode === 'signin' && 'Sign In'}
            {mode === 'signup' && 'Sign Up'}
            {mode === 'forgot' && 'Send Reset Link'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignInPage;

